import React from "react";
import { SelectItem, Input, Switch, Button, Textarea } from "@heroui/react";
import AppSelect from "../common/appSelect";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  UserIcon,
  IdentificationIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { TFunction } from "react-i18next";
import { State, ValidationErrors } from "../../types/types";
import { formatSportName } from "../../utils/utilMethods";
import hoowlrLogo from "../../assets/images/logo.svg";
import loginBanner from "../../assets/images/login-banner.png";

const labelClass =
  "!text-slate-600 lg:!text-[#71717A] data-[invalid=true]:!text-[#71717A] group-data-[invalid=true]:!text-[#71717A] text-xs lg:text-sm font-semibold lg:font-medium leading-4 lg:leading-5 text-left self-start pb-0 after:hidden";

const inputClassNames = {
  base: "gap-1",
  label: labelClass,
  inputWrapper:
    "h-10 lg:h-9 min-h-10 lg:min-h-9 rounded-xl border border-slate-200 lg:border-[#EBEBEC] bg-white lg:bg-[#EBEBEC] px-3.5 lg:px-3 shadow-[0_1px_2px_rgba(0,0,0,0.03)] lg:shadow-none data-[hover=true]:border-slate-300 lg:data-[hover=true]:bg-[#EBEBEC] group-data-[focus=true]:bg-white lg:group-data-[focus=true]:bg-[#EBEBEC] group-data-[focus=true]:border-[#2563EB]",
  input: "text-xs sm:text-sm leading-5 placeholder:text-slate-400 lg:placeholder:text-[#71717A]",
  errorMessage: "text-left ml-1 w-full text-xs",
};

const selectClassNames = {
  base: "gap-1",
  label: labelClass,
  trigger:
    "h-10 lg:h-9 min-h-10 lg:min-h-9 rounded-xl border border-slate-200 lg:border-[#EBEBEC] bg-white lg:bg-[#EBEBEC] px-3.5 lg:px-3 shadow-[0_1px_2px_rgba(0,0,0,0.03)] lg:shadow-none data-[hover=true]:border-slate-300 lg:data-[hover=true]:bg-[#EBEBEC] data-[focus=true]:border-[#2563EB] data-[open=true]:border-[#2563EB]",
  value:
    "text-xs sm:text-sm leading-5 text-[#18181B] group-data-[has-value=false]:text-slate-400 lg:group-data-[has-value=false]:text-[#71717A]",
  selectorIcon: "text-[#71717A]",
  errorMessage: "text-left ml-1 w-full text-xs",
};

const unitSelectClassNames = {
  base: "gap-1 w-24 shrink-0",
  label: labelClass,
  trigger:
    "h-10 lg:h-9 min-h-10 lg:min-h-9 rounded-xl border border-slate-200 lg:border-[#EBEBEC] bg-white lg:bg-[#EBEBEC] px-2.5 shadow-[0_1px_2px_rgba(0,0,0,0.03)] lg:shadow-none data-[hover=true]:border-slate-300 lg:data-[hover=true]:bg-[#EBEBEC] data-[focus=true]:border-[#2563EB] data-[open=true]:border-[#2563EB]",
  value: "text-xs sm:text-sm text-[#18181B]",
  selectorIcon: "text-[#71717A]",
};

const textareaClassNames = {
  base: "gap-1",
  label: labelClass,
  inputWrapper:
    "rounded-xl border border-slate-200 lg:border-[#EBEBEC] bg-white lg:bg-[#EBEBEC] px-3.5 lg:px-3 shadow-[0_1px_2px_rgba(0,0,0,0.03)] lg:shadow-none data-[hover=true]:border-slate-300 lg:data-[hover=true]:bg-[#EBEBEC] group-data-[focus=true]:bg-white lg:group-data-[focus=true]:bg-[#EBEBEC] group-data-[focus=true]:border-[#2563EB]",
  input: "text-xs sm:text-sm leading-5 placeholder:text-slate-400 lg:placeholder:text-[#71717A]",
  errorMessage: "text-left ml-1 w-full text-xs",
};

const tertiaryButtonClass =
  "h-10 rounded-2xl lg:rounded-3xl bg-[#EBEBEC] px-4 text-xs sm:text-sm lg:text-base font-semibold lg:font-medium leading-5 lg:leading-6 text-[#18181B] hover:bg-[#dcdcde] active:scale-[0.98] transition-all";

interface FieldProps {
  min: number;
  max: number;
  placeholder: string;
}

interface FighterFormBasicStageProps {
  t: TFunction<"signUpSetupStages", undefined>;
  state: State;
  sports: string[];
  weightClasses: string[];
  selectedTrainingDisciplines: Set<string> | any;
  selectedWeightClasses: Set<string> | any;
  validationErrors: ValidationErrors;
  heightFieldProps: FieldProps;
  weightFieldProps: FieldProps;
  reachFieldProps: FieldProps;
  loadSportsIfNeeded: () => Promise<void>;
  loadweightclassIfNeeded: () => Promise<void>;
  toggleMultiSelectOption: (field: string | any, values: string[] | any) => void;
  handleFormChange: (value: any, field: string | any, dto: string | any) => void;
  handleNext: (
    e: React.FormEvent<HTMLFormElement> | any,
    nextStage: string | any
  ) => void;
  handleBack: () => void;
  formatText: (text: string) => string;
}

const FighterFormBasicStage: React.FC<FighterFormBasicStageProps> = ({
  t,
  state,
  sports,
  weightClasses,
  selectedTrainingDisciplines,
  selectedWeightClasses,
  validationErrors,
  heightFieldProps,
  weightFieldProps,
  reachFieldProps,
  loadSportsIfNeeded,
  loadweightclassIfNeeded,
  toggleMultiSelectOption,
  handleFormChange,
  handleNext,
  handleBack,
  formatText,
}) => {
  return (
    <div className="flex min-h-dvh w-full bg-white font-['Plus_Jakarta_Sans',sans-serif] text-[#18181B]">
      {/* LEFT COLUMN */}
      <div className="flex min-w-0 flex-1 flex-col">
        <div
          className="
            flex min-h-dvh flex-col justify-between
            px-6 py-6 sm:px-12 sm:py-8
            lg:py-9
            [@media(min-height:701px)_and_(max-height:800px)]:lg:py-6
            [@media(max-height:700px)]:lg:py-3
          "
        >
          {/* Header: logo */}
          <div className="flex w-full items-center justify-between">
            <img
              src={hoowlrLogo}
              alt="Hoowlr"
              className="
                h-9 w-auto object-contain object-left
                lg:h-16 lg:w-48
                [@media(min-height:701px)_and_(max-height:800px)]:lg:h-14
                [@media(max-height:700px)]:lg:h-10
              "
            />
          </div>

          {/* Centered content */}
          <div className="flex flex-1 flex-col items-center justify-center my-auto w-full py-2">
            <div className="flex w-full flex-col items-center gap-1 sm:gap-2 text-center mb-4 sm:mb-6 lg:mb-8 [@media(max-height:700px)]:mb-2">
              <h1 className="text-xl sm:text-2xl lg:text-4xl font-bold lg:font-extrabold leading-tight lg:leading-10 text-[#18181B]">
                {t("fighterFormBasic.title")}
              </h1>
              <p className="text-xs sm:text-sm lg:text-base leading-4 lg:leading-6 text-[#71717A]">
                {t("fighterFormBasic.subtitle")}
              </p>
            </div>

            <form
              noValidate
              onSubmit={(e) => handleNext(e, "fighterFormDetails")}
              className="
                flex w-full max-w-96 flex-col gap-4 sm:gap-5 lg:gap-6
                [@media(min-height:701px)_and_(max-height:800px)]:gap-4 lg:[@media(min-height:701px)_and_(max-height:800px)]:gap-5
                [@media(max-height:700px)]:gap-3 lg:[@media(max-height:700px)]:gap-4
              "
            >
              <div
                className="
                  flex flex-col gap-3 lg:gap-4
                  [@media(max-height:700px)]:gap-2.5 lg:[@media(max-height:700px)]:gap-3
                "
              >
                <AppSelect
                  name="trainingDisciplines"
                  label={t("fighterFormBasic.trainingDisciplines")}
                  onOpenChange={(isOpen) => isOpen && loadSportsIfNeeded()}
                  labelPlacement="outside"
                  placeholder={t("fighterFormBasic.trainingDisciplinesPlaceholder")}
                  selectionMode="multiple"
                  selectedKeys={selectedTrainingDisciplines}
                  onSelectionChange={(keys) => {
                    const values = Array.from(keys) as string[];
                    toggleMultiSelectOption("trainingDisciplines", values);
                  }}
                  isRequired
                  isInvalid={!!validationErrors["fighterDto.trainingDisciplines"]}
                  errorMessage={validationErrors["fighterDto.trainingDisciplines"]}
                  className="w-full"
                  classNames={selectClassNames}
                  startContent={
                    <IdentificationIcon className="w-4 h-4 text-gray-400 pointer-events-none flex-shrink-0" />
                  }
                >
                  {sports.map((discipline) => (
                    <SelectItem key={discipline}>
                      {formatSportName(discipline)}
                    </SelectItem>
                  ))}
                </AppSelect>

                <AppSelect
                  name="weightClasses"
                  label={t("fighterFormBasic.weightClasses")}
                  labelPlacement="outside"
                  placeholder={t("fighterFormBasic.weightClassesPlaceholder")}
                  selectionMode="multiple"
                  selectedKeys={selectedWeightClasses}
                  onOpenChange={(isOpen) => {
                    if (isOpen && weightClasses.length === 0) {
                      setTimeout(() => loadweightclassIfNeeded(), 0);
                    }
                  }}
                  onSelectionChange={(keys) => {
                    const values = Array.from(keys) as string[];
                    toggleMultiSelectOption("weightClasses", values);
                  }}
                  isRequired
                  isInvalid={!!validationErrors["fighterDto.weightClasses"]}
                  errorMessage={validationErrors["fighterDto.weightClasses"]}
                  className="w-full"
                  classNames={selectClassNames}
                  startContent={
                    <IdentificationIcon className="w-4 h-4 text-gray-400 pointer-events-none flex-shrink-0" />
                  }
                >
                  {weightClasses.map((weightClass) => (
                    <SelectItem key={weightClass}>
                      {formatText(weightClass)}
                    </SelectItem>
                  ))}
                </AppSelect>

                {/* Height & Unit */}
                <div className="flex items-start gap-2">
                  <Input
                    type="number"
                    name="height"
                    label={t("fighterFormBasic.height")}
                    labelPlacement="outside"
                    placeholder={heightFieldProps.placeholder}
                    value={
                      state.formData.fighterDto?.height !== undefined
                        ? String(state.formData.fighterDto.height)
                        : ""
                    }
                    onValueChange={(value) =>
                      handleFormChange(value, "height", "fighterDto")
                    }
                    isRequired
                    min={heightFieldProps.min}
                    max={heightFieldProps.max}
                    step={0.1}
                    isInvalid={!!validationErrors["fighterDto.height"]}
                    errorMessage={validationErrors["fighterDto.height"]}
                    className="flex-1"
                    classNames={inputClassNames}
                    startContent={
                      <UserIcon className="w-4 h-4 text-gray-400 pointer-events-none flex-shrink-0" />
                    }
                  />

                  <AppSelect
                    name="heightUnit"
                    label={t("fighterFormBasic.heightUnit")}
                    labelPlacement="outside"
                    selectedKeys={
                      state.formData.fighterDto?.heightUnit
                        ? [state.formData.fighterDto.heightUnit]
                        : ["cm"]
                    }
                    onChange={(e) => {
                      handleFormChange(e.target.value, "heightUnit", "fighterDto");
                      handleFormChange("", "height", "fighterDto");
                    }}
                    isRequired
                    classNames={unitSelectClassNames}
                  >
                    <SelectItem key="cm">cm</SelectItem>
                    <SelectItem key="ft">ft</SelectItem>
                    <SelectItem key="inch">inch</SelectItem>
                  </AppSelect>
                </div>

                {/* Weight & Unit */}
                <div className="flex items-start gap-2">
                  <Input
                    type="number"
                    name="weight"
                    label={t("fighterFormBasic.weight")}
                    labelPlacement="outside"
                    placeholder={weightFieldProps.placeholder}
                    value={
                      state.formData.fighterDto?.weight !== undefined
                        ? String(state.formData.fighterDto.weight)
                        : ""
                    }
                    onValueChange={(value) =>
                      handleFormChange(value, "weight", "fighterDto")
                    }
                    isRequired
                    min={weightFieldProps.min}
                    max={weightFieldProps.max}
                    step={0.1}
                    isInvalid={!!validationErrors["fighterDto.weight"]}
                    errorMessage={validationErrors["fighterDto.weight"]}
                    className="flex-1"
                    classNames={inputClassNames}
                    startContent={
                      <UserIcon className="w-4 h-4 text-gray-400 pointer-events-none flex-shrink-0" />
                    }
                  />

                  <AppSelect
                    name="weightUnit"
                    label={t("fighterFormBasic.weightUnit")}
                    labelPlacement="outside"
                    selectedKeys={
                      state.formData.fighterDto?.weightUnit
                        ? [state.formData.fighterDto.weightUnit]
                        : ["kgs"]
                    }
                    onChange={(e) => {
                      handleFormChange(e.target.value, "weightUnit", "fighterDto");
                      handleFormChange("", "weight", "fighterDto");
                    }}
                    classNames={unitSelectClassNames}
                  >
                    <SelectItem key="kgs">kgs</SelectItem>
                    <SelectItem key="lbs">lbs</SelectItem>
                  </AppSelect>
                </div>

                {/* Reach & Unit */}
                <div className="flex items-start gap-2">
                  <Input
                    type="number"
                    name="reach"
                    label={t("fighterFormBasic.reach")}
                    labelPlacement="outside"
                    placeholder={reachFieldProps.placeholder}
                    value={
                      state.formData.fighterDto?.reach !== undefined
                        ? String(state.formData.fighterDto.reach)
                        : ""
                    }
                    onValueChange={(value) =>
                      handleFormChange(value, "reach", "fighterDto")
                    }
                    isRequired
                    min={reachFieldProps.min}
                    max={reachFieldProps.max}
                    step={0.1}
                    isInvalid={!!validationErrors["fighterDto.reach"]}
                    errorMessage={validationErrors["fighterDto.reach"]}
                    className="flex-1"
                    classNames={inputClassNames}
                    startContent={
                      <UserIcon className="w-4 h-4 text-gray-400 pointer-events-none flex-shrink-0" />
                    }
                  />

                  <AppSelect
                    name="reachUnit"
                    label={t("fighterFormBasic.reachUnit")}
                    labelPlacement="outside"
                    selectedKeys={
                      state.formData.fighterDto?.reachUnit
                        ? [state.formData.fighterDto.reachUnit]
                        : ["cm"]
                    }
                    onChange={(e) => {
                      handleFormChange(e.target.value, "reachUnit", "fighterDto");
                      handleFormChange("", "reach", "fighterDto");
                    }}
                    isRequired
                    classNames={unitSelectClassNames}
                  >
                    <SelectItem key="cm">cm</SelectItem>
                    <SelectItem key="ft">ft</SelectItem>
                    <SelectItem key="inch">inch</SelectItem>
                  </AppSelect>
                </div>

                {/* Stance */}
                <AppSelect
                  name="stance"
                  label={t("fighterFormBasic.stance")}
                  labelPlacement="outside"
                  placeholder={t("fighterFormBasic.stancePlaceholder")}
                  selectedKeys={
                    state.formData.fighterDto?.stance
                      ? [state.formData.fighterDto.stance]
                      : []
                  }
                  onChange={(e) =>
                    handleFormChange(e.target.value, "stance", "fighterDto")
                  }
                  isRequired
                  isInvalid={!!validationErrors["fighterDto.stance"]}
                  errorMessage={validationErrors["fighterDto.stance"]}
                  className="w-full"
                  classNames={selectClassNames}
                  startContent={
                    <IdentificationIcon className="w-4 h-4 text-gray-400 pointer-events-none flex-shrink-0" />
                  }
                >
                  <SelectItem key="Orthodox">Orthodox</SelectItem>
                  <SelectItem key="Southpaw">Southpaw</SelectItem>
                  <SelectItem key="Switch">Switch</SelectItem>
                </AppSelect>

                {/* Title Holder Switch */}
                <div className="w-full pt-1">
                  <label className="text-xs sm:text-sm font-semibold lg:font-medium text-slate-600 lg:text-[#71717A] mb-1.5 block">
                    {t("fighterFormBasic.titleHolder")}
                  </label>
                  <div className="flex items-center gap-4">
                    <Switch
                      isSelected={state.formData.fighterDto?.isTitleHolder ?? false}
                      onValueChange={(value) =>
                        handleFormChange(value, "isTitleHolder", "fighterDto")
                      }
                      color="primary"
                    >
                      <span className="text-xs sm:text-sm font-medium text-[#18181B]">
                        {state.formData.fighterDto?.isTitleHolder
                          ? t("fighterFormBasic.yes")
                          : t("fighterFormBasic.no")}
                      </span>
                    </Switch>
                  </div>
                  {validationErrors["fighterDto.isTitleHolder"] && (
                    <p className="mt-1 text-xs text-red-500 text-left ml-1">
                      {validationErrors["fighterDto.isTitleHolder"]}
                    </p>
                  )}
                </div>

                {/* Achievements List */}
                <div className="space-y-3 pt-2">
                  {state.formData.fighterDto?.achievementDetails?.map(
                    (achievement, index) => (
                      <div
                        key={index}
                        className="flex flex-col gap-2 rounded-xl border border-slate-200 lg:border-[#EBEBEC] bg-slate-50 lg:bg-[#F9FAFB] p-3.5"
                      >
                        <Textarea
                          name={`achievementDetails-${index}`}
                          label={`${t("fighterFormBasic.achievements")} #${index + 1}`}
                          labelPlacement="outside"
                          placeholder={t("fighterFormBasic.achievementsPlaceholder")}
                          value={achievement.text}
                          onValueChange={(value) => {
                            const updatedAchievements = [
                              ...(state.formData.fighterDto?.achievementDetails || []),
                            ];
                            updatedAchievements[index] = { text: value };
                            handleFormChange(
                              updatedAchievements,
                              "achievementDetails",
                              "fighterDto"
                            );
                          }}
                          maxLength={5000}
                          className="w-full"
                          classNames={textareaClassNames}
                        />
                        <Button
                          variant="flat"
                          size="sm"
                          className="self-end rounded-xl bg-red-50 text-red-500 hover:bg-red-100 text-xs font-medium px-3 py-1.5 transition-all"
                          onClick={() => {
                            const updatedAchievements = [
                              ...(state.formData.fighterDto?.achievementDetails || []),
                            ];
                            updatedAchievements.splice(index, 1);
                            handleFormChange(
                              updatedAchievements,
                              "achievementDetails",
                              "fighterDto"
                            );
                          }}
                        >
                          {t("remove")}
                        </Button>
                      </div>
                    )
                  )}

                  <Button
                    size="sm"
                    variant="bordered"
                    startContent={<PlusIcon className="w-4 h-4" />}
                    onClick={() => {
                      const updated = [
                        ...(state.formData.fighterDto?.achievementDetails || []),
                      ];
                      updated.push({ text: "" });
                      handleFormChange(updated, "achievementDetails", "fighterDto");
                    }}
                    className="w-full rounded-xl border border-[#0485F7] text-[#0485F7] text-xs sm:text-sm font-semibold h-9 hover:bg-[#0485F7]/10 transition-all"
                  >
                    {t("addAchievement")}
                  </Button>
                </div>
              </div>

              {/* Footer: Back / Next */}
              <div className="flex w-full items-center justify-between gap-3 pt-4 sm:pt-6 [@media(max-height:700px)]:pt-3">
                <Button
                  onPress={handleBack}
                  variant="flat"
                  startContent={<ArrowLeftIcon className="h-4 w-4" />}
                  className={tertiaryButtonClass}
                >
                  {t("navigation.back")}
                </Button>

                <Button
                  type="submit"
                  endContent={<ArrowRightIcon className="h-4 w-4" />}
                  className="h-10 rounded-2xl lg:rounded-3xl bg-[#0485F7] px-4 text-xs sm:text-sm lg:text-base font-semibold lg:font-medium leading-5 lg:leading-6 text-white hover:bg-[#0374d6] active:scale-[0.99] transition-all shadow-sm disabled:cursor-not-allowed disabled:bg-gray-400"
                >
                  {t("navigation.next")}
                </Button>
              </div>
            </form>
          </div>

          {/* Footer */}
          <p className="pt-2 pb-1 text-center text-[11px] sm:text-xs font-normal leading-4 text-[#71717A]">
            {t("footer_copyright", "© Hoowlr 2026, All rights reserved")}
          </p>
        </div>
      </div>

      {/* RIGHT BANNER (hidden below lg) */}
      <div className="hidden shrink-0 p-9 lg:sticky lg:top-0 lg:flex lg:h-dvh lg:w-[45%] xl:w-[55%] 2xl:w-[62%]">
        <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-[2rem] bg-[#1F6BFF]">
          <img
            src={loginBanner}
            alt=""
            className="max-h-full max-w-full object-contain"
          />
        </div>
      </div>
    </div>
  );
};

export default FighterFormBasicStage;
